import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Paper, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { addClientNote, getClientNotes } from '@/lib/api/clients';
import { showErrorToast, showSuccessToast } from '@/utils/toast-utils';

interface ClientDetailsV2FlagsTabProps {
  clientId: string;
  shouldOpenAddNote?: boolean;
  onAddNoteHandled?: () => void;
}

interface ClientDetailsV2NotesSectionProps extends ClientDetailsV2FlagsTabProps {
  sectionTitle?: string;
  showTypeSelector?: boolean;
  showFlagLabels?: boolean;
  compact?: boolean;
  sx?: React.ComponentProps<typeof Box>['sx'];
  listMaxHeight?: number | string;
  previewLimit?: number;
  onViewAll?: () => void;
}

type FlagType = 'urgent' | 'warning' | 'note';

interface FlagItem {
  id: string;
  type: FlagType;
  title: string;
  description: string;
  createdAt: string | null;
}

interface ClientNoteResponse {
  id?: string | null;
  title?: string | null;
  content?: string | null;
  flag?: string | null;
  createdAt?: string | null;
}

interface ClientNotesResponse {
  notes?: ClientNoteResponse[];
}

const QUESTIONNAIRE_SUBMISSION_TITLE_PREFIX = /^questionnaire submission:\s*/i;

const normalizeFlagType = (value: string | null | undefined): FlagType => {
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'urgent' || normalized === 'warning' || normalized === 'note') {
    return normalized;
  }
  return 'note';
};

const getTypeStyles = (type: FlagType) => {
  switch (type) {
    case 'urgent':
      return {
        bg: '#fff4f4',
        border: '#fecdd3',
        iconBg: '#ff3b4f',
        labelColor: '#ef4444',
        label: 'URGENT'
      };
    case 'warning':
      return {
        bg: '#fffaf0',
        border: '#f3d48a',
        iconBg: '#f59e0b',
        labelColor: '#f59e0b',
        label: 'WARNING'
      };
    case 'note':
      return {
        bg: '#f3f7ff',
        border: '#bdd7ff',
        iconBg: '#3b82f6',
        labelColor: '#3b82f6',
        label: 'NOTE'
      };
  }
};

function isQuestionnaireSubmissionNote(title: string): boolean {
  return QUESTIONNAIRE_SUBMISSION_TITLE_PREFIX.test(title.trim());
}

function getQuestionnaireSubmissionTitle(title: string): string {
  const trimmed = title.trim();
  const match = trimmed.match(QUESTIONNAIRE_SUBMISSION_TITLE_PREFIX);
  if (!match) return trimmed;
  const questionnaireName = trimmed.slice(match[0].length).trim();
  return questionnaireName || 'Questionnaire';
}

function formatNoteDate(value?: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

export const ClientDetailsV2NotesSection: React.FC<ClientDetailsV2NotesSectionProps> = ({
  clientId,
  shouldOpenAddNote,
  onAddNoteHandled,
  sectionTitle,
  showTypeSelector = true,
  showFlagLabels = true,
  compact = false,
  sx,
  listMaxHeight,
  previewLimit,
  onViewAll,
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [selectedType, setSelectedType] = useState<FlagType>('note');
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [flags, setFlags] = useState<FlagItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (shouldOpenAddNote) {
      setIsAdding(true);
      if (onAddNoteHandled) {
        onAddNoteHandled();
      }
    }
  }, [shouldOpenAddNote, onAddNoteHandled]);

  const fetchNotes = async () => {
    try {
      setLoading(true);
      const data = await getClientNotes(clientId) as ClientNotesResponse;
      const mappedFlags = (data.notes || []).map((note, index) => ({
        id: String(note.id || `flag-${index}`),
        type: normalizeFlagType(note.flag),
        title: note.title || '',
        description: note.content || '',
        createdAt: note.createdAt || null,
      }));

      mappedFlags.sort((first, second) => {
        const firstTime = first.createdAt ? new Date(first.createdAt).getTime() : 0;
        const secondTime = second.createdAt ? new Date(second.createdAt).getTime() : 0;
        return secondTime - firstTime;
      });

      setFlags(mappedFlags);
    } catch (err: any) {
      console.error('Failed to fetch notes:', err);
      showErrorToast(err, 'Failed to fetch notes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchNotes();
  }, [clientId]);

  const resetForm = () => {
    setTitle('');
    setNotes('');
    setSelectedType('note');
  };

  const handleSave = async () => {
    if (!notes.trim() && !title.trim()) {
      showErrorToast(new Error('Note content or title cannot be empty'), 'Note content or title cannot be empty');
      return;
    }

    try {
      setSaving(true);
      
      await addClientNote(clientId, {
        title: title.trim(),
        content: notes.trim(),
        flag: selectedType,
      });

      showSuccessToast('Success', 'Note added successfully');

      // Reset form
      resetForm();
      setIsAdding(false);

      // Refresh list
      await fetchNotes();
    } catch (err: any) {
      console.error('Failed to save note:', err);
      showErrorToast(err, 'Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  const typeOptions: { value: FlagType; label: string; color: string; selectedColor: string; selectedBg: string; selectedBorder: string }[] = [
    { value: 'urgent', label: 'Urgent', color: '#ef4444', selectedColor: '#ef4444', selectedBg: '#fff4f4', selectedBorder: '#fecdd3' },
    { value: 'warning', label: 'Warning', color: '#f59e0b', selectedColor: '#f59e0b', selectedBg: '#fffaf0', selectedBorder: '#f3d48a' },
    { value: 'note', label: 'Note', color: '#24c2bf', selectedColor: '#24c2bf', selectedBg: '#ecfdfc', selectedBorder: '#7edcd7' },
  ];
  const visibleFlags =
    previewLimit && previewLimit > 0 ? flags.slice(0, previewLimit) : flags;
  const shouldShowViewAll = Boolean(onViewAll && flags.length > 0);

  return (
    <Box sx={{ mt: 2.5, ...sx }}>
      {/* Header and Add Button */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: sectionTitle ? 'space-between' : 'flex-end', gap: 1.5, mb: compact ? 1.25 : 1.75 }}>
        {sectionTitle && (
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#0f172a', fontFamily: 'Inter, sans-serif' }}>
            {sectionTitle}
          </Typography>
        )}
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setIsAdding(true)}
          sx={{
            bgcolor: '#25a8da',
            color: '#fff',
            textTransform: 'none',
            borderRadius: '10px',
            px: compact ? 1.5 : 2,
            py: compact ? 0.75 : 1,
            minHeight: compact ? 36 : 40,
            fontSize: compact ? '0.8125rem' : '0.875rem',
            fontWeight: 600,
            boxShadow: 'none',
            '&:hover': { bgcolor: '#1498ca', boxShadow: 'none' },
          }}
        >
          Add Note
        </Button>
      </Box>

      {/* Add Form */}
      {isAdding && (
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: '10px',
            border: '2px solid #28a8de',
            bgcolor: '#fff',
          }}
        >
          <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#1e293b', mb: 2 }}>
            New Note
          </Typography>

          {/* Type Selection */}
          {showTypeSelector && (
            <Box sx={{ display: 'flex', gap: 1.25, mb: 2, flexWrap: 'wrap' }}>
              {typeOptions.map((opt) => {
                const isSelected = selectedType === opt.value;
                return (
                  <Box
                    key={opt.value}
                    onClick={() => setSelectedType(opt.value)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.75,
                      py: 0.625,
                      borderRadius: '20px',
                      cursor: 'pointer',
                      border: '1px solid',
                      borderColor: isSelected ? opt.selectedBorder : '#d5dde5',
                      bgcolor: isSelected ? opt.selectedBg : '#fff',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: isSelected ? opt.selectedColor : opt.color,
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: isSelected ? 500 : 400,
                        color: isSelected ? '#334155' : '#475569',
                      }}
                    >
                      {opt.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}

          {/* Inputs */}
          <TextField
            fullWidth
            placeholder="Title..."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            variant="outlined"
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                fontSize: '0.875rem',
                bgcolor: '#fff',
                '& fieldset': { borderColor: '#d8e1e8' },
                '&:hover fieldset': { borderColor: '#c2ced9' },
                '&.Mui-focused fieldset': { borderColor: '#25a8da', borderWidth: '1px' },
              },
            }}
          />

          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Notes..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            variant="outlined"
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                fontSize: '0.875rem',
                bgcolor: '#fff',
                '& fieldset': { borderColor: '#d8e1e8' },
                '&:hover fieldset': { borderColor: '#c2ced9' },
                '&.Mui-focused fieldset': { borderColor: '#25a8da', borderWidth: '1px' },
              },
            }}
          />

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              disabled={saving}
              onClick={handleSave}
              sx={{
                bgcolor: '#25a8da',
                color: '#fff',
                textTransform: 'none',
                borderRadius: '10px',
                px: 2.5,
                py: 0.8,
                minWidth: 76,
                fontSize: '0.875rem',
                fontWeight: 600,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#1498ca', boxShadow: 'none' },
              }}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button
              onClick={() => {
                setIsAdding(false);
                resetForm();
              }}
              disabled={saving}
              sx={{
                color: '#25a8da',
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
              }}
            >
              Cancel
            </Button>
          </Box>
        </Paper>
      )}

      {/* List */}
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        maxHeight: listMaxHeight,
        overflowY: listMaxHeight ? 'auto' : undefined,
        pr: listMaxHeight ? (compact ? 1.75 : 0.75) : undefined,
        scrollbarGutter: listMaxHeight ? 'stable' : undefined,
        scrollbarWidth: listMaxHeight ? 'thin' : undefined,
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-thumb': {
          borderRadius: '999px',
          bgcolor: '#cbd5e1',
        },
        '&::-webkit-scrollbar-track': {
          bgcolor: 'transparent',
        },
      }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: compact ? 2.5 : 4 }}>
            <CircularProgress size={compact ? 22 : 40} />
          </Box>
        ) : flags.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: compact ? 2.5 : 4,
              minHeight: compact ? 94 : undefined,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              borderRadius: compact ? '8px' : '10px',
              border: compact ? '1px dashed #d5e3eb' : '1px solid #dbe3ea',
              bgcolor: compact ? '#fbfdff' : '#fff',
            }}
          >
            <Typography sx={{ color: '#64748b', fontSize: compact ? '0.8125rem' : undefined, lineHeight: 1.35 }}>
              No notes found for this client.
            </Typography>
          </Paper>
        ) : (
          visibleFlags.map((flag) => {
            const styles = getTypeStyles(flag.type);
            const isQuestionnaireNote = isQuestionnaireSubmissionNote(flag.title);
            const questionnaireTitle = isQuestionnaireNote
              ? getQuestionnaireSubmissionTitle(flag.title)
              : flag.title;
            const dateLabel = formatNoteDate(flag.createdAt);

            if (isQuestionnaireNote) {
              return (
                <Paper
                  key={flag.id}
                  elevation={0}
                  sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    ...(compact
                      ? { p: 1.5, borderRadius: '8px', pl: 1.75 }
                      : { p: 2.1, borderRadius: '12px', pl: 2.35 }),
                    border: '1px solid #d7e8f0',
                    bgcolor: '#fff',
                    display: 'flex',
                    gap: compact ? 1 : 1.5,
                    alignItems: 'flex-start',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 4,
                      bgcolor: '#25a8da',
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: compact ? 28 : 36,
                      height: compact ? 28 : 36,
                      borderRadius: compact ? '8px' : '10px',
                      bgcolor: '#e8f7fc',
                      flexShrink: 0,
                    }}
                  >
                    <AssignmentOutlinedIcon sx={{ fontSize: compact ? 15 : 18, color: '#25a8da' }} />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        justifyContent: 'space-between',
                        gap: 1,
                        flexWrap: 'wrap',
                        mb: 0.65,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
                        <Typography
                          sx={{
                            px: 0.9,
                            py: 0.25,
                            borderRadius: '999px',
                            bgcolor: '#eef7fb',
                            color: '#0f7699',
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 700,
                            fontSize: compact ? '0.625rem' : '0.6875rem',
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                          }}
                        >
                          Questionnaire
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: compact ? '0.875rem' : '0.9375rem',
                            fontWeight: 600,
                            color: '#0f172a',
                            lineHeight: 1.3,
                            overflowWrap: 'anywhere',
                            fontFamily: 'Inter, sans-serif',
                          }}
                        >
                          {questionnaireTitle}
                        </Typography>
                      </Box>
                      {dateLabel ? (
                        <Typography
                          sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            color: '#98a2b3',
                            flexShrink: 0,
                          }}
                        >
                          {dateLabel}
                        </Typography>
                      ) : null}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: compact ? '0.8125rem' : '0.9375rem',
                        color: '#334155',
                        lineHeight: compact ? 1.4 : 1.55,
                        whiteSpace: 'pre-wrap',
                        overflowWrap: 'anywhere',
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >
                      {flag.description || 'No additional details provided.'}
                    </Typography>
                  </Box>
                </Paper>
              );
            }

            return (
              <Paper
                key={flag.id}
                elevation={0}
                sx={{
                  ...(compact ? { p: 1.5, borderRadius: '8px' } : { p: 2.1, borderRadius: '10px' }),
                  border: '1px solid',
                  borderColor: compact ? '#dbe3ea' : styles.border,
                  bgcolor: compact ? '#fff' : styles.bg,
                  display: 'flex',
                  gap: compact ? 1 : 1.5,
                  alignItems: 'flex-start',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: compact ? 28 : 34,
                    height: compact ? 28 : 34,
                    borderRadius: compact ? '8px' : '10px',
                    bgcolor: compact ? '#eaf8fd' : styles.iconBg,
                    flexShrink: 0,
                  }}
                >
                  <ChatBubbleOutlineRoundedIcon sx={{ fontSize: compact ? 15 : 18, color: compact ? '#25a8da' : '#ffffff' }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    {showFlagLabels && (
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: styles.labelColor, letterSpacing: 0.3 }}>
                        {styles.label}
                      </Typography>
                    )}
                    {flag.title && (
                      <Typography sx={{ fontSize: compact ? '0.875rem' : '1rem', fontWeight: 600, color: '#1e293b', lineHeight: 1.2, overflowWrap: 'anywhere' }}>
                        {flag.title}
                      </Typography>
                    )}
                  </Box>
                  <Typography sx={{ fontSize: compact ? '0.8125rem' : '0.95rem', color: '#475569', lineHeight: compact ? 1.35 : 1.45, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                    {flag.description || 'No additional details provided.'}
                  </Typography>
                </Box>
              </Paper>
            );
          })
        )}
      </Box>
      {shouldShowViewAll && (
        <Button
          type="button"
          variant="text"
          size="small"
          onClick={onViewAll}
          endIcon={<ChevronRightIcon sx={{ fontSize: 16 }} />}
          sx={{
            mt: compact ? 1.25 : 1.75,
            alignSelf: 'flex-start',
            minHeight: 30,
            px: 0,
            py: 0,
            borderRadius: 0,
            color: '#2CA7D6',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 600,
            fontSize: '0.8125rem',
            lineHeight: 1.2,
            textTransform: 'none',
            '&:hover': {
              bgcolor: 'transparent',
              color: '#148dbc',
              textDecoration: 'underline',
            },
            '& .MuiButton-endIcon': {
              ml: 0.25,
            },
          }}
        >
          View all
        </Button>
      )}
    </Box>
  );
};

export const ClientDetailsV2FlagsTab: React.FC<ClientDetailsV2FlagsTabProps> = (props) => (
  <ClientDetailsV2NotesSection {...props} />
);
